import { useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  externalDownDetectorApiRef,
  VendorStatus,
} from '@internal/backstage-plugin-external-down-detector';

// Matches AUTO_REFRESH_INTERVAL_MS in
// plugins/external-down-detector/src/components/ExternalDownDetectorPage/useStatusPolling.ts
// so both surfaces refresh on the same cadence.
const POLL_INTERVAL_MS = 30_000;

export interface UseDownDetectorAlertsResult {
  downVendors: VendorStatus[];
  degradedVendors: VendorStatus[];
}

interface VendorState {
  downVendors: VendorStatus[];
  degradedVendors: VendorStatus[];
}

const INITIAL_STATE: VendorState = { downVendors: [], degradedVendors: [] };

export function useDownDetectorAlerts(): UseDownDetectorAlertsResult {
  const api = useApi(externalDownDetectorApiRef);
  const [state, setState] = useState<VendorState>(INITIAL_STATE);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const poll = async () => {
      try {
        const snapshot = await api.getStatus();
        if (!mountedRef.current) return;
        const vendors = snapshot?.vendors ?? [];
        setState({
          downVendors: vendors.filter(v => v.level === 'down'),
          degradedVendors: vendors.filter(v => v.level === 'degraded'),
        });
      } catch {
        // Fail silently and keep the last known state; the next poll will retry.
      }
    };

    void poll();
    const interval = setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [api]);

  return state;
}
